import { BranchStateService, ProjectService } from '@alpha-core/platform-ui';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { TASK_STATUSES } from './constants/task-status.constants';

const CURRENT_CASE_INSTANCE = 'currentCaseInstance';
interface IFetchAllRequest {
    pageNumber?: number;
    pageSize?: number;
    enquiryFields?: Record<string, any>[];
    taskNames?: string[];
    mappingPaths: Record<string, any>[];
}
@Injectable({ providedIn: 'root' })
export class CaseService {
    private httpClient = inject(HttpClient);
    private projectService = inject(ProjectService);

    private baseUrl = `${environment.ALPHA_UI_ADMIN_SERVICE_URL}/case-server/`;
    private dataSubject = new BehaviorSubject<any>(null);
    dashBoardData$ = this.dataSubject.asObservable();

    /**
     * Build HttpParams with branch parameter for branch isolation.
     * Uses BranchStateService to get branch from URL (defaults to 'main' if not present).
     */
    private buildParams(): HttpParams {
        const branch = BranchStateService.getBranchFromUrl();
        return new HttpParams().set('branch', branch);
    }

    setCurrentCase(caseInstance: any) {
        window.alpha['project']['currentCaseInstance'] = JSON.stringify(caseInstance);
    }

    fetchCurrentCase() {
        try {
            return JSON.parse(window.alpha['project']['currentCaseInstance'] || '{}');
        } catch (error) {
            console.error(error);
        }
        return {};
    }

    getAllTaskName() {
        const currentCaseInstance = this.fetchCurrentCase();
        if (currentCaseInstance.taskInstance) {
            return currentCaseInstance.taskInstance.taskName;
        }
        return null;
    }

    fetchAdminTasks(fetchAllCaseWithTask: {
        pageNumber?: any;
        pageSize?: any;
        enquiryFields?: Record<string, any>[];
        excludeTaskNames?: string[];
        mappingPaths: Record<string, any>[];
    }): any {
        const project = this.projectService.fetchCurrentProject();
        const enquiry: Record<string, any> = {};
        if (fetchAllCaseWithTask.enquiryFields?.length) {
            enquiry['projectId'] = project.projectId;
            enquiry['enable'] = true;
            enquiry['filters'] = fetchAllCaseWithTask.enquiryFields;
        }
        return this.httpClient.post(
            `${this.baseUrl}case/instance/fetch-all`,
            {
                pageNumber: fetchAllCaseWithTask.pageNumber,
                pageSize: fetchAllCaseWithTask.pageSize,
                caseType: project.caseType || project.name,
                status: TASK_STATUSES,
                enquiry,
                excludeTaskNames: fetchAllCaseWithTask.excludeTaskNames,
                projectId: project.projectId,
                mappingPaths: fetchAllCaseWithTask.mappingPaths,
            },
            { params: this.buildParams() },
        );
    }

    fetchTasksCount(fetchAllCaseWithTask: {
        enquiryFields?: Record<string, any>[];
        taskNames?: string[];
        excludeTaskNames?: string[];
    }) {
        const project = this.projectService.fetchCurrentProject();
        let requestBody: any = {
            caseType: project.caseType || project.name,
            status: fetchAllCaseWithTask.taskNames?.length ? ['READY'] : TASK_STATUSES,
            taskNames: fetchAllCaseWithTask.taskNames,
            excludeTaskNames: fetchAllCaseWithTask.excludeTaskNames,
        };
        if (fetchAllCaseWithTask.enquiryFields?.length) {
            const enquiry: Record<string, any> = {};
            enquiry['projectId'] = project.projectId;
            enquiry['enable'] = true;
            enquiry['filters'] = fetchAllCaseWithTask.enquiryFields;
            requestBody.enquiry = enquiry;
        }

        return this.httpClient.post(`${this.baseUrl}case/instance/count`, requestBody, { params: this.buildParams() });
    }

    fetchSignals(fetchAllCaseWithTask: {
        pageNumber?: any;
        pageSize?: any;
        enquiryFields?: Record<string, any>[];
        mappingPaths: Record<string, any>[];
    }): any {
        const project = this.projectService.fetchCurrentProject();
        const enquiry: Record<string, any> = {};
        if (fetchAllCaseWithTask.enquiryFields?.length) {
            enquiry['projectId'] = project.projectId;
            enquiry['enable'] = true;
            enquiry['filters'] = fetchAllCaseWithTask.enquiryFields;
        }
        return this.httpClient.post(
            `${this.baseUrl}signal/fetch-all`,
            {
                options: {
                    pageNumber: fetchAllCaseWithTask.pageNumber,
                    pageSize: fetchAllCaseWithTask.pageSize,
                },
                caseType: project.caseType || project.name,
                status: 'CREATED',
                enquiry,
                projectId: project.projectId,
                mappingPaths: fetchAllCaseWithTask.mappingPaths,
            },
            { params: this.buildParams() },
        );
    }

    delegateCases(delegateCaseData: Record<string, any>[]) {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient.post(`${this.baseUrl}case/instance/delegate-all`, {
            projectId: project?.projectId,
            delegateCaseData,
        });
    }

    sendSignal(sendSignalData: Record<string, any>[]) {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient.post(`${this.baseUrl}case/instance/signal/send-all`, {
            projectId: project?.projectId,
            sendSignalData,
        });
    }

    fetchDashboardStatus(options: Record<string, any> = {}) {
        const project = this.projectService.fetchCurrentProject();
        return this.httpClient
            .post(
                `${this.baseUrl}case/instance/dashboard`,
                {
                    caseType: project?.caseType,
                    ...(options || {}),
                },
                {
                    headers: { 'alpha-hide-spinner': 'true' },
                    params: this.buildParams(),
                },
            )
            .subscribe((data) => {
                this.dataSubject.next(data);
            });
    }

    reAttempt(data: { caseInstances: any; clientId: string }) {
        const project = this.projectService.fetchCurrentProject();
        const getCurrentTag = project?.currentTag ? true : false;
        const requestBody = {
            projectId: project?.projectId,
            clientId: data.clientId,
            getCurrentTag,
            tasks: data?.caseInstances.map((el: any) => {
                return { cid: el.caseInstance.cid, taskId: el.taskInstance.taskId };
            }),
        };
        return this.httpClient.post(`${this.baseUrl}case/instance/re-attempt`, requestBody, {
            params: this.buildParams(),
        });
    }

    async fetchVariables(processInstanceId: number) {
        const requestBody = {
            processInstanceId,
        };
        return this.httpClient.post(`${this.baseUrl}case/instance/variables`, requestBody);
    }

    fetchReAttemptTasks(fetchAllCaseWithTask: IFetchAllRequest): any {
        const project = this.projectService.fetchCurrentProject();
        const enquiry: Record<string, any> = {};
        if (fetchAllCaseWithTask.enquiryFields?.length) {
            enquiry['projectId'] = project.projectId;
            enquiry['enable'] = true;
            enquiry['filters'] = fetchAllCaseWithTask.enquiryFields;
        }
        return this.httpClient.post(
            `${this.baseUrl}re-attempt/fetch-all`,
            {
                pageNumber: fetchAllCaseWithTask.pageNumber,
                pageSize: fetchAllCaseWithTask.pageSize,
                caseType: project.caseType || project.name,
                status: fetchAllCaseWithTask.taskNames?.length ? ['READY'] : TASK_STATUSES,
                enquiry,
                taskNames: fetchAllCaseWithTask.taskNames,
                projectId: project.projectId,
                mappingPaths: fetchAllCaseWithTask.mappingPaths,
            },
            { params: this.buildParams() },
        );
    }

    reStrucetureMappingData(inboxTask: any, selectedDropdown: string) {
        const additionalMappings = Object.values(inboxTask.hooks).flatMap(
            (hook: any) =>
                hook.triggers
                    ?.filter((t: any) => t.type === 'custom-code' || t.type === 'global-custom-code')
                    .flatMap((t: any) => t.config?.additionalMapping || []) || [],
        );
        return [...inboxTask[selectedDropdown].columns, ...additionalMappings]
            .filter(({ mappingType }: { mappingType: string }) => mappingType === 'co')
            .map(({ mappingType, mapping, type }: { mappingType: string; mapping: string; type: string }) => ({
                mappingType,
                mapping,
                type,
            }));
    }
}
